import type { ZzfxParams } from './zzfx';

/**
 * Named ZzFX one-shots (zzfx.ts builds them, sfxBank.ts plays them through the mixer). Only for events
 * that were silent before: the hand-built sounds in oneShots.ts / uiSounds.ts stay where they were.
 *
 * Keep the 1970 feel: no bare square/triangle beeps and no pitch jumps (that is chiptune). Use noise
 * (shape 4), sines, a low-ish ZzFX filter (negative = lowpass) and our own `lp` on top, short and quiet.
 * To tweak: paste `zz` into the designer (https://killedbyapixel.github.io/ZzFX/), adjust, paste back,
 * then `npx tsx scripts/render-sfx.ts` writes every preset to .tmp/sfx/*.wav to listen to.
 *
 * zz = [volume, randomness, frequency, attack, sustain, release, shape, shapeCurve, slide, deltaSlide,
 *       pitchJump, pitchJumpTime, repeatTime, noise, modulation, bitCrush, delay, sustainVolume, decay,
 *       tremolo, filter]
 */
export interface SfxPreset {
  zz: ZzfxParams;
  /** Output level into the bus (after the ZzFX volume). */
  gain: number;
  /** Extra Web Audio lowpass (Hz) to take the digital edge off. */
  lp?: number;
  /** 'ui' plays even while paused (menus); 'sfx' is a world sound (positional when given a position). */
  bus: 'sfx' | 'ui';
  /** Distance (m) at which a positional sound is at half level. */
  ref?: number;
  /** Send into the street slap-back (0..1). */
  echo?: number;
}

export const SFX_PRESETS = {
  /** A gun picked up off the pavement: a heavy, dull metal clack in the hand. */
  pickupWeapon: { zz: [1, 0.05, 150, 0, 0.012, 0.13, 4, 1, -6, 0, 0, 0, 0, 0.4, 0, 0, 0.045, 0.7, 0.012, 0, -1600], gain: 0.55, lp: 2600, bus: 'sfx' },
  /** Weapon in hand changed: a leather-and-steel draw, two soft ticks. */
  weaponSwap: { zz: [1, 0.1, 620, 0, 0, 0.035, 4, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0.055, 1, 0.004, 0, -2400], gain: 0.32, lp: 3000, bus: 'sfx' },
  /** Aim lock found a target: a quiet double click, like a hammer going back. */
  lockOn: { zz: [1, 0.05, 900, 0, 0, 0.02, 4, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0.07, 1, 0.003, 0, -3200], gain: 0.26, lp: 3400, bus: 'ui' },
  /** An armed officer draws a bead on you: a revolver hammer cocking, heard where he stands. */
  hammerCock: { zz: [1, 0.05, 700, 0, 0, 0.025, 4, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0.11, 1, 0.003, 0, -3600], gain: 0.5, lp: 3800, bus: 'sfx', ref: 16 },
  /** A street lamp knocked out: the mains fizz and sputter after the glass. */
  lampFizz: { zz: [1, 0.1, 100, 0, 0.22, 0.35, 2, 1, -0.3, 0, 0, 0, 0.055, 0.35, 0, 0, 0, 0.5, 0, 0.8, -1400], gain: 0.22, lp: 1800, bus: 'sfx', ref: 20 },
  /** A lamp post / signal / beacon pole bent over: a low, ringing metal clang. */
  poleClang: { zz: [1, 0.08, 170, 0, 0.02, 0.55, 0, 1, -0.6, 0, 0, 0, 0, 0.12, 37, 0, 0, 0.7, 0.04, 0, -2200], gain: 0.45, lp: 2600, bus: 'sfx', ref: 22, echo: 0.12 },
  /** Garage work done (tyres, tune, brakes): a spanner ratchet. */
  ratchet: { zz: [1, 0.05, 1300, 0, 0.32, 0.04, 4, 1, 0, 0, 0, 0, 0.04, 0, 0, 0, 0, 1, 0, 1, -3000], gain: 0.3, lp: 3200, bus: 'ui' },
  /** A roller shutter going up (spray bay open): a dull corrugated rattle rising. */
  shutterUp: { zz: [1, 0.05, 110, 0.05, 0.6, 0.2, 4, 1, 0.35, 0, 0, 0, 0.028, 0.5, 0, 0, 0, 0.8, 0, 0.6, -1100], gain: 0.4, lp: 1500, bus: 'ui' },
  /** The shutter coming down (spray bay done): the rattle falling, ending on the floor. */
  shutterDown: { zz: [1, 0.05, 150, 0.02, 0.45, 0.22, 4, 1, -0.35, 0, 0, 0, 0.028, 0.5, 0, 0, 0, 0.8, 0, 0.6, -1100], gain: 0.4, lp: 1500, bus: 'ui' },
  /** Spray can shaken: the ball bearing rattling inside. */
  canRattle: { zz: [1, 0.1, 2200, 0, 0.18, 0.04, 4, 1, 0, 0, 0, 0, 0.065, 0, 0, 0, 0, 1, 0, 1, -4200], gain: 0.22, lp: 4200, bus: 'ui' },
  /** Spray can: the hiss of paint going on. */
  sprayHiss: { zz: [1, 0, 2600, 0.04, 0.55, 0.12, 4, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 900], gain: 0.16, lp: 5200, bus: 'ui' },
  /** Phone box: the heavy handset lifted off the cradle. */
  handset: { zz: [1, 0.05, 120, 0, 0.01, 0.1, 4, 1, -3, 0, 0, 0, 0, 0.5, 0, 0, 0.08, 1, 0.01, 0, -900], gain: 0.5, lp: 1400, bus: 'ui' },
  /** One of your rounds went into somebody: a short, muffled thwack (the hit marker's sound; bullets landing were silent). */
  hitMark: { zz: [1, 0.05, 210, 0, 0, 0.05, 4, 1, 0, 0, 0, 0, 0, 0.2, 0, 0, 0.03, 1, 0.004, 0, -1700], gain: 0.34, lp: 1500, bus: 'ui' },
  /** A few coins (small cash, under the register's threshold): a soft pocket jingle. */
  coins: { zz: [1, 0.15, 2400, 0, 0, 0.12, 0, 1, 0, 0, 0, 0, 0, 0.08, 410, 0, 0.05, 1, 0.01, 0, -4800], gain: 0.13, lp: 5000, bus: 'ui' },
  /** A car catching fire: the petrol going up in a soft, rising whoosh (under the small boom). */
  fireWhoosh: { zz: [1, 0.05, 260, 0.14, 0.26, 0.55, 4, 1, 1.2, 0, 0, 0, 0, 0.7, 0, 0, 0.04, 0.7, 0, 0, -1300], gain: 0.5, lp: 2600, bus: 'sfx', ref: 22, echo: 0.1 },
  /** Armed response authorised: a burst of police-band radio from a passing car, choppy and crunchy. */
  radioSquelch: { zz: [1, 0.1, 900, 0.005, 0.28, 0.06, 4, 1, 0, 0, 0, 0, 0.045, 0.6, 0, 0.3, 0, 0.8, 0, 0.5, 700], gain: 0.26, lp: 3200, bus: 'ui' },
} as const satisfies Record<string, SfxPreset>;

export type SfxName = keyof typeof SFX_PRESETS;
export const SFX_NAMES = Object.keys(SFX_PRESETS) as SfxName[];
